import { ImportSourceFieldType } from '@today/types'
import type { ChangeEvent } from 'react'

const INPUT_CLASS =
	'h-9 w-full rounded-ds-md border border-ds-border bg-ds-surface px-2 text-ds-sm text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring'

/** One source field as the editor shows it: its name, type and where it is read from. */
export interface SourceFieldRow {
	name: string
	type: ImportSourceFieldType
	required: boolean
	/** Where the field sits in the file, e.g. `1–6`, `column codigo`, `$.geo.city`. */
	location: string
}

/**
 * Props for {@link SourceFieldsEditor}.
 *
 * @property fields - The source fields, in format order.
 * @property onChange - Called with the field at `index` and its replacement.
 */
export interface SourceFieldsEditorProps {
	fields: readonly SourceFieldRow[]
	onChange: (index: number, field: Pick<SourceFieldRow, 'name' | 'type' | 'required'>) => void
}

interface FieldRowProps {
	index: number
	field: SourceFieldRow
	onChange: SourceFieldsEditorProps['onChange']
}

function FieldRow({ index, field, onChange }: FieldRowProps) {
	return (
		<tr>
			<td>
				<input
					aria-label={`Name of field ${index + 1}`}
					className={INPUT_CLASS}
					value={field.name}
					onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(index, { ...field, name: e.target.value })}
				/>
			</td>
			<td>{field.location}</td>
			<td>
				<select
					aria-label={`Type of field ${index + 1}`}
					className={INPUT_CLASS}
					value={field.type}
					onChange={(e) => onChange(index, { ...field, type: e.target.value as ImportSourceFieldType })}
				>
					{Object.values(ImportSourceFieldType).map((type) => (
						<option key={type} value={type}>
							{type}
						</option>
					))}
				</select>
			</td>
			<td>
				<input
					type="checkbox"
					aria-label={`Field ${index + 1} is required`}
					checked={field.required}
					onChange={(e) => onChange(index, { ...field, required: e.target.checked })}
				/>
			</td>
		</tr>
	)
}

/** The source fields of the format: rename, retype, mark required (TO-400 SCN-008). Pure. */
export function SourceFieldsEditor({ fields, onChange }: SourceFieldsEditorProps) {
	if (fields.length === 0) return <p className="text-ds-sm text-ds-text">No source fields yet.</p>
	return (
		<table className="w-full text-left text-ds-sm text-ds-text">
			<thead>
				<tr>
					<th scope="col">Field</th>
					<th scope="col">Position</th>
					<th scope="col">Type</th>
					<th scope="col">Required</th>
				</tr>
			</thead>
			<tbody>
				{fields.map((field, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: a field is identified by its position while it is renamed
					<FieldRow key={index} index={index} field={field} onChange={onChange} />
				))}
			</tbody>
		</table>
	)
}
